/**
 * /organizations/compare?ids=slug1,slug2,slug3
 *
 * Side-by-side comparison of 2–4 charities. Users get here via the
 * "Compare (N)" floating action bar on `/organizations` after checking
 * multiple cards.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { http } from "@/lib/api";
import {
  ArrowLeft, ExternalLink, Shield, Heart, Users, Globe2, Loader2,
  MapPin, Calendar, TrendingUp, X,
} from "lucide-react";

const TIER_LABEL = { bronze: "Bronze", silver: "Silver", gold: "Gold", platinum: "Platinum", verified: "Verified" };

function OrgLogo({ org, size = 72 }) {
  const [failed, setFailed] = useState(false);
  const src = org.logo_url || org.image_url;
  const monogram = (org.acronym || org.name || "?").replace(/[^A-Za-z]/g, "").slice(0, 2).toUpperCase() || "?";
  const style = { width: size, height: size };
  if (!src || failed) {
    return (
      <div className="rounded-xl grid place-items-center shrink-0 mx-auto font-mono font-bold" style={{ ...style, background: "var(--wofy-surface)", color: "var(--wofy-primary)", fontSize: size / 3 }}>
        {monogram}
      </div>
    );
  }
  return (
    <div className="rounded-xl overflow-hidden shrink-0 border grid place-items-center mx-auto" style={{ ...style, borderColor: "var(--wofy-border)", background: "var(--wofy-surface)" }}>
      <img src={src} alt={org.name} className="max-w-full max-h-full object-contain" onError={() => setFailed(true)} />
    </div>
  );
}

export default function OrganizationsCompare() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const ids = useMemo(() => (params.get("ids") || "").split(",").filter(Boolean), [params]);
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (ids.length === 0) { setOrgs([]); setLoading(false); return; }
    setLoading(true); setErr(null);
    http.get(`/organizations/compare?ids=${ids.join(",")}`)
      .then((r) => setOrgs(r.data.organizations || []))
      .catch((e) => setErr(e?.response?.data?.detail || "Couldn't load organizations"))
      .finally(() => setLoading(false));
  }, [ids.join(",")]); // eslint-disable-line react-hooks/exhaustive-deps

  const removeOne = (slugOrId) => {
    const next = ids.filter((k) => k !== slugOrId);
    if (next.length < 2) return navigate("/organizations");
    setParams({ ids: next.join(",") });
  };

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10" data-testid="orgs-compare-page">
      <Link
        to="/organizations"
        className="inline-flex items-center gap-2 text-sm hover:underline"
        style={{ color: "var(--wofy-primary)" }}
        data-testid="orgs-compare-back"
      >
        <ArrowLeft className="w-4 h-4" /> Back to organizations
      </Link>

      <div className="mt-4">
        <div className="overline">Social Pulse · Compare</div>
        <h1 className="font-display text-4xl sm:text-5xl mt-2">Compare charities</h1>
        <p className="mt-3 max-w-2xl text-sm" style={{ color: "var(--wofy-text-soft)" }}>
          Side-by-side comparison of mission, causes, geographic reach, verification tier, and how to donate or volunteer.
          Add up to 4 organizations from the directory to compare.
        </p>
      </div>

      {loading && (
        <div className="mt-10 flex items-center gap-2 text-sm" style={{ color: "var(--wofy-text-soft)" }}>
          <Loader2 className="w-4 h-4 animate-spin" /> Loading comparison…
        </div>
      )}

      {err && (
        <div className="mt-6 rounded-xl border p-4 text-sm" style={{ borderColor: "#fca5a5", background: "#fef2f2", color: "#7f1d1d" }}>
          {err}
        </div>
      )}

      {!loading && !err && orgs.length === 0 && (
        <div className="mt-10 wofy-card text-center py-12" data-testid="orgs-compare-empty">
          <div className="font-display text-2xl">Nothing to compare yet</div>
          <p className="text-sm mt-2" style={{ color: "var(--wofy-text-soft)" }}>
            Head to the organizations directory and check 2 or more charities to compare.
          </p>
          <Link to="/organizations" className="wofy-btn-primary mt-5 inline-flex">Browse organizations</Link>
        </div>
      )}

      {!loading && orgs.length > 0 && (
        <CompareTable orgs={orgs} onRemove={removeOne} />
      )}
    </div>
  );
}

function CompareTable({ orgs, onRemove }) {
  const cols = orgs.length;
  const gridCols = cols === 2 ? "grid-cols-[220px_1fr_1fr]" : cols === 3 ? "grid-cols-[200px_1fr_1fr_1fr]" : "grid-cols-[180px_1fr_1fr_1fr_1fr]";

  return (
    <div className="mt-8 overflow-x-auto" data-testid="orgs-compare-table">
      <div className={`grid ${gridCols} gap-0 rounded-2xl border overflow-hidden`} style={{ borderColor: "var(--wofy-border)", minWidth: 200 * cols + 220 }}>
        {/* Header row: logos, names, remove buttons */}
        <div className="p-5 border-b border-r" style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-surface)" }}>
          <div className="text-xs uppercase font-mono tracking-widest opacity-70">Organization</div>
        </div>
        {orgs.map((o) => (
          <div
            key={`head-${o.id}`}
            className="p-5 border-b relative text-center"
            style={{ borderColor: "var(--wofy-border)", borderRight: "1px solid var(--wofy-border)" }}
          >
            <button
              onClick={() => onRemove(o.slug || o.id)}
              data-testid={`orgs-compare-remove-${o.slug || o.id}`}
              className="absolute top-2 right-2 w-6 h-6 rounded-full inline-flex items-center justify-center hover:bg-black/5"
              title="Remove from comparison"
            >
              <X className="w-3.5 h-3.5" />
            </button>
            <OrgLogo org={o} size={64} />
            {o.website_url ? (
              <a
                href={o.website_url}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 font-display text-base leading-tight inline-flex items-center gap-1 hover:underline"
              >
                {o.name} <ExternalLink className="w-3 h-3 opacity-60" />
              </a>
            ) : (
              <div className="mt-3 font-display text-base leading-tight">{o.name}</div>
            )}
            {o.acronym && o.acronym !== o.name && (
              <div className="text-[10px] font-mono uppercase tracking-wider opacity-60 mt-1">{o.acronym}</div>
            )}
            <Link
              to={`/organizations/${o.slug || o.id}`}
              className="mt-3 inline-block text-[11px] font-mono uppercase tracking-wider hover:underline"
              style={{ color: "var(--wofy-primary)" }}
              data-testid={`orgs-compare-profile-${o.slug || o.id}`}
            >
              View full profile →
            </Link>
          </div>
        ))}

        {/* Rows */}
        <Row label="Verification tier" icon={Shield}>
          {orgs.map((o) => (
            <Cell key={`tier-${o.id}`}>
              {o.verification_tier ? (
                <span
                  className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider"
                  style={{ background: "var(--wofy-primary)", color: "#FDFBF7" }}
                >
                  <Shield className="w-3 h-3" /> {TIER_LABEL[o.verification_tier] || o.verification_tier}
                </span>
              ) : <Muted>—</Muted>}
            </Cell>
          ))}
        </Row>

        <Row label="Rating" icon={TrendingUp}>
          {orgs.map((o) => (
            <Cell key={`rating-${o.id}`}>
              {typeof o.rating === "number" ? (
                <span className="font-display text-2xl">{o.rating.toFixed(1)}<span className="text-xs opacity-60"> / 5</span></span>
              ) : <Muted>—</Muted>}
            </Cell>
          ))}
        </Row>

        <Row label="Founded" icon={Calendar}>
          {orgs.map((o) => (
            <Cell key={`founded-${o.id}`}>
              {o.year_founded ? (
                <div>
                  <div className="text-base font-semibold">{o.year_founded}</div>
                  {o.years_active && <div className="text-[10px] font-mono uppercase opacity-60 mt-0.5">{o.years_active} years</div>}
                </div>
              ) : <Muted>—</Muted>}
            </Cell>
          ))}
        </Row>

        <Row label="Reach" icon={Globe2}>
          {orgs.map((o) => (
            <Cell key={`reach-${o.id}`}>{o.reach ? <span className="text-sm">{o.reach}</span> : <Muted>—</Muted>}</Cell>
          ))}
        </Row>

        <Row label="Headquarters" icon={MapPin}>
          {orgs.map((o) => (
            <Cell key={`hq-${o.id}`}>
              {o.hq_city || o.city || o.hq_country || o.country ? (
                <span className="text-sm">
                  {[o.hq_city || o.city, o.hq_country || o.country].filter(Boolean).join(", ")}
                </span>
              ) : <Muted>—</Muted>}
            </Cell>
          ))}
        </Row>

        <Row label="Causes">
          {orgs.map((o) => (
            <Cell key={`causes-${o.id}`}>
              {o.causes?.length > 0 ? (
                <div className="flex flex-wrap gap-1 justify-center">
                  {o.causes.slice(0, 8).map((c) => (
                    <span key={c} className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full" style={{ background: "var(--wofy-surface)", color: "var(--wofy-text-soft)" }}>{c}</span>
                  ))}
                  {o.causes.length > 8 && <span className="text-[10px] font-mono opacity-50">+{o.causes.length - 8}</span>}
                </div>
              ) : <Muted>—</Muted>}
            </Cell>
          ))}
        </Row>

        <Row label="Mission" tall>
          {orgs.map((o) => (
            <Cell key={`mission-${o.id}`} tall>
              {o.mission || o.tagline ? (
                <p className="text-sm leading-relaxed text-left" style={{ color: "var(--wofy-text-soft)" }}>
                  {o.mission || o.tagline}
                </p>
              ) : <Muted>—</Muted>}
            </Cell>
          ))}
        </Row>

        <Row label="Actions" icon={Heart}>
          {orgs.map((o) => (
            <Cell key={`actions-${o.id}`}>
              <div className="flex flex-col gap-2 items-center">
                {o.donation_url && (
                  <a
                    href={o.donation_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="wofy-btn-primary text-xs w-full justify-center"
                    data-testid={`orgs-compare-donate-${o.slug || o.id}`}
                  >
                    <Heart className="w-3.5 h-3.5" /> Donate
                  </a>
                )}
                {o.volunteer_url && (
                  <a
                    href={o.volunteer_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="wofy-btn-ghost text-xs w-full justify-center"
                    data-testid={`orgs-compare-volunteer-${o.slug || o.id}`}
                  >
                    <Users className="w-3.5 h-3.5" /> Volunteer
                  </a>
                )}
                {!o.donation_url && !o.volunteer_url && <Muted>No links</Muted>}
              </div>
            </Cell>
          ))}
        </Row>
      </div>

      <div className="mt-6 text-[11px] font-mono uppercase tracking-wider inline-flex items-center gap-1 opacity-60">
        <ExternalLink className="w-3 h-3" /> Donation and volunteer links open the official organization website in a new tab.
      </div>
    </div>
  );
}

function Row({ label, icon: Icon, children, tall = false }) {
  return (
    <>
      <div
        className={"px-5 border-t border-r flex items-start gap-2 " + (tall ? "py-5" : "py-4")}
        style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-surface)" }}
      >
        {Icon && <Icon className="w-3.5 h-3.5 mt-0.5 opacity-70" />}
        <span className="text-xs font-mono uppercase tracking-wider">{label}</span>
      </div>
      {children}
    </>
  );
}

function Cell({ children, tall = false }) {
  return (
    <div
      className={"px-5 border-t border-r text-center " + (tall ? "py-5" : "py-4")}
      style={{ borderColor: "var(--wofy-border)" }}
    >
      {children}
    </div>
  );
}

function Muted({ children }) {
  return <span className="text-sm opacity-40">{children}</span>;
}
